import { useState, useEffect } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, PhoneCall, Loader2, CheckCircle2, Copy, Play } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceConfig = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);
  
  const user = JSON.parse(localStorage.getItem('fruitycall_user') || '{}');
  
  useEffect(() => {
    loadSettings();
    loadRecentMessages();
  }, []);
  
  const loadSettings = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/voice/settings?user_email=${encodeURIComponent(user.email)}`
      );
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };
  
  const loadRecentMessages = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/voice/messages?user_email=${encodeURIComponent(user.email)}&limit=5`
      );
      const data = await response.json();
      setRecentMessages(data.messages || []);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };
  
  const simulateCall = async () => {
    setSimulating(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/voice/simulate-call?user_email=${encodeURIComponent(user.email)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success("Appel simulé reçu ! Transcription en cours...");
        
        // Recharger les messages après 3 secondes
        setTimeout(() => {
          loadRecentMessages();
        }, 3000);
      }
    } catch (error) {
      console.error("Error simulating call:", error);
      toast.error("Erreur lors de la simulation");
    } finally {
      setSimulating(false);
    }
  };
  
  const copyNumber = () => {
    if (settings?.fruitycall_number) {
      navigator.clipboard.writeText(settings.fruitycall_number);
      toast.success("Numéro copié !");
    }
  };
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <AppHeader />
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }
  
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader />
      
      <main className="flex-1">
        <div className="container-custom py-8">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold">Réception d'appels</h1>
            <p className="text-muted-foreground">
              Configurez votre numéro Fruitycall pour recevoir et transcrire vos messages vocaux
            </p>
          </div>
          
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Fruitycall Number Card */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-primary" />
                  <CardTitle>Votre numéro Fruitycall</CardTitle>
                </div>
                <CardDescription>
                  Communiquez ce numéro à vos patients pour recevoir leurs messages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Numéro attribué</p>
                      <p className="text-2xl font-bold text-primary">
                        {settings?.fruitycall_number}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyNumber}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Comment ça marche ?</h4>
                  <ol className="space-y-1 text-sm text-muted-foreground">
                    <li>1. Votre patient appelle ce numéro</li>
                    <li>2. Il laisse un message vocal</li>
                    <li>3. Le message est automatiquement transcrit</li>
                    <li>4. Vous le consultez dans votre espace</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
            
            {/* Test Card */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <PhoneCall className="h-5 w-5 text-secondary" />
                  <CardTitle>Tester le système</CardTitle>
                </div>
                <CardDescription>
                  Simulez la réception d'un appel pour tester le fonctionnement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={simulateCall}
                  disabled={simulating}
                  className="w-full"
                  size="lg"
                >
                  {simulating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Simulation en cours...
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Simuler un appel entrant
                    </>
                  )}
                </Button>
                
                <div className="rounded-lg bg-muted/50 p-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>Mode démonstration :</strong> Cette fonction simule la réception
                    d'un appel et sa transcription automatique. En production, les vrais appels
                    arriveraient via Twilio ou un service similaire.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Recent Messages */}
          <div className="mt-8">
            <Card className="border-2">
              <CardHeader>
                <CardTitle>Messages récents</CardTitle>
                <CardDescription>
                  Les 5 derniers messages vocaux reçus et transcrits
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recentMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Phone className="mb-2 h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Aucun message reçu pour le moment
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Testez le système avec le bouton ci-dessus
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentMessages.map((msg) => (
                      <div
                        key={msg._id}
                        className="flex items-start gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">
                              {msg.caller_name || msg.caller_phone}
                            </span>
                            {msg.is_new && (
                              <Badge className="bg-accent/20 text-accent-foreground text-xs">
                                Nouveau
                              </Badge>
                            )}
                            {msg.transcription_status === 'processing' && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Transcription...
                              </Badge>
                            )}
                            {msg.transcription_status === 'completed' && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <CheckCircle2 className="h-3 w-3 text-success" />
                                Transcrit
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {msg.transcription || "Transcription en cours..."}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{new Date(msg.created_at).toLocaleString('fr-FR')}</span>
                            {msg.recording_duration && (
                              <>
                                <span>•</span>
                                <span>{formatDuration(msg.recording_duration)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Technical Info */}
          <div className="mt-8">
            <Card className="border-2 border-muted">
              <CardHeader>
                <CardTitle className="text-lg">Intégration technique</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div>
                  <strong className="text-foreground">Service de téléphonie :</strong>
                  <p>Twilio ou Vonage pour la réception d'appels et l'enregistrement des messages vocaux</p>
                </div>
                <div>
                  <strong className="text-foreground">Service de transcription :</strong>
                  <p>OpenAI Whisper ou Google Speech-to-Text pour convertir l'audio en texte</p>
                </div>
                <div>
                  <strong className="text-foreground">Webhook :</strong>
                  <p className="font-mono text-xs bg-muted p-2 rounded mt-1">
                    POST {BACKEND_URL}/api/voice/webhook/incoming
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VoiceConfig;
